import React, { useEffect, useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  FormControlLabel,
  CircularProgress,
  Autocomplete,
  styled,
  Box,
  Card,
  CardHeader,
  IconButton,
  Divider,
  Collapse,
  CardContent,
  Chip
} from '@mui/material';
import { DatePicker, DateTimePicker } from "@mui/lab";
import { ControlsClient, ModelEntityDTO } from '../../core/ControlsApi/api';
import { getIn } from 'formik';
import * as Yup from 'yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import JoditEditor from 'jodit-react';
import { REACT_APP_PORTAL_API_URL } from '../../KeyVariables';

const controlsClient = new ControlsClient(REACT_APP_PORTAL_API_URL);

export const isFieldRequired = (validationSchema, name) => {
  if(validationSchema) {
    const schemaDescription = validationSchema.describe()
  const accessor = name.split(".").join(".fields.")
  const field = getIn(schemaDescription.fields, accessor)
  if (!field) {
    return false
  }
  const isRequired = field.tests.some((test) => test.name === "required")
  return isRequired
  } else return false;
}
const DynamicField: React.FC<any> = ({ field, formik, initialValue,validationSchema, onChange }) => {
  const [options, setOptions] = useState<Array<ModelEntityDTO>>([]);
  const [fieldValue, setFieldValue] = useState(initialValue || '');
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const { i18n } = useTranslation();

  const fetchOptions = async () => {
    try {
      if (field.fetchData) {
        const data = await field.fetchData();
        setOptions(data);
      } else if (field.namespace) {
        const data = await controlsClient.getmodelvalues(field.namespace, i18n.language, field.translatable);
        setOptions(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!field.shouldBeRerendered) {
      if ((field.fetchData || field.namespace) && (field.type === 'select' || field.type === 'multiselect' || field.type === 'offlineAutocomplete')) {
        fetchOptions();
      }
    }
  }, []);

  useEffect(() => {
    if (field.shouldBeRerendered || field.translatable) {
      if ((field.fetchData || field.namespace) && (field.type === 'select' || field.type === 'multiselect' || field.type === 'offlineAutocomplete')) {
        fetchOptions();
      }
    }
  }, [field.fetchData, i18n.language]);

  useEffect(() => {
    if (formik && formik.getFieldProps(field.name).value !== undefined) {
      setFieldValue(formik.getFieldProps(field.name).value);
      const selectedOption = options?.find(option => option.id === formik.getFieldProps(field.name).value);
      if (selectedOption) {
        setInputValue(selectedOption.name);
      }
    }
  }, [formik.getFieldProps(field.name).value, options]);

  useEffect(() => {
    if (initialValue !== undefined) {
      setFieldValue(initialValue);
      const selectedOption = options?.find(option => option.id === initialValue);
      if (selectedOption) {
        setInputValue(selectedOption.name);
      }
    }
  }, [initialValue, options]);

  useEffect(() => {
    if (fieldValue && field.type === 'autocomplete' && !options.find(x => x.id == fieldValue)) {
      const loadOptions = async () => {
        if (field.fetchData) {
          const data = await field.fetchData();
          setOptions(data);
        } else if(field.namespace && field.namespace != '') {
          const option = await controlsClient.getmodelvaluebyid(field.namespace, fieldValue);
          if (option) {
            setInputValue(option.name);
          }
        }
      };
      loadOptions();
    }
  }, [fieldValue]);


  useEffect(() => {
    if (field.type === 'autocomplete') {
      const delayDebounce = setTimeout(() => {
        const fetchAutocompleteOptions = async () => {
            setLoading(true);
            try {
              if (field.fetchDataAutoComplete) {
                const result = await field.fetchDataAutoComplete(inputValue, formik.values);
                setOptions(result);
              }
              else if (field.namespace) {
                const newOptions = await controlsClient.autocomplete(field.namespace, inputValue);
                setOptions(newOptions);
              }  
            } catch (error) {
              console.error(error);
            } finally {
              setLoading(false);
            }
        };

        fetchAutocompleteOptions();
      }, 1000);

      return () => clearTimeout(delayDebounce);
    }
  }, [inputValue, field.namespace, field.fetchDataAutoComplete]);

  if (field.isVisible && !field.isVisible(formik ? formik.values : {})) {
    return null;
  }

  const isError = formik ? Boolean(getIn(formik.errors, field.name)) : false;
  const isRequired = isFieldRequired(validationSchema, field.name);
  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    setFieldValue(value);
    formik.setFieldValue(field.name, value);
    if (onChange) {
      onChange(event);
    }
    if (field.customChangeFunction) {
      field.customChangeFunction(value, formik);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setFieldValue(isChecked);
    if (formik) {
      formik.setFieldValue(field.name, isChecked);
    }
    if (onChange) {
      onChange(event);
    }
    if (field.customChangeFunction) {
      field.customChangeFunction(isChecked, formik);
    }
  };

  const handleDateChange = (date: any) => {
    setFieldValue(date);
    if (onChange) {
      onChange(date);
    }
    if (formik) {
      formik.setFieldValue(field.name, date);
    }
    if (field.customChangeFunction) {
      field.customChangeFunction(date, formik);
    }
  };

  const handleMultiselectChange = (event: SelectChangeEvent<any>) => {
    const selectedIds = (event.target.value as number[]).filter(id => id !== 0);
    const idsString = selectedIds.join(',').trim();
    setFieldValue(idsString);
    if (formik) {
      formik.setFieldValue(field.name, idsString);
    }
  };
  const handleEditorChange = newContent => {
    formik.setFieldValue(field.name, newContent);
    if (onChange) {
        onChange(newContent);
    }
};

  const commonTextFieldProps = {
    fullWidth: true,
    InputLabelProps: { shrink: Boolean(fieldValue) || field.inputProps },
    label: !field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null,
    name: field.name,
    InputProps: { 
      readOnly: field.readonly, 
      ...field.inputProps, 
      inputProps: { maxLength: field.maxLength }, 
      endAdornment: field.loading ? <CircularProgress size={20} style={{ position: 'absolute', right: 10 }} /> : null,
    },
    value: fieldValue,
    disabled: field.loading,
    onChange: handleChange,
    error: isError,
  };

  switch (field.type) {
    case "string":
    case "textarea":
    case "number":
      return <>
        <TextField {...commonTextFieldProps} autoFocus={field.autoFocus}
        type={field.type === "number" ? "number" : "text"} multiline={field.type === "textarea"}
        minRows={field.type === "textarea" ? 3 : undefined} />
      </>;

    case "select":
      return (
        <FormControl fullWidth 
        error={isError}>
     <InputLabel id={`${field.name}-label`}
     >{!field.removeCaption ? `${field.label} ${isRequired ? ' *' : ''}` : null}</InputLabel>
          <Select labelId={`${field.name}-label`} label={!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null} name={field.name} inputProps={{ readOnly: field.readonly }} value={fieldValue} onChange={handleChange}>
            {options?.map(option => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
          </Select>
        </FormControl>
      );
    case "multiselect":
      return (
        <FormControl fullWidth error={isError}>
          <InputLabel id={`${field.name}-label`}>{!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null}</InputLabel>
          <Select labelId={`${field.name}-label`} name={field.name} label={!field.removeCaption ? field.label : null} multiple inputProps={{ readOnly: field.readonly }} value={fieldValue?.split(',').map(Number) || []} onChange={handleMultiselectChange} renderValue={(selected) => selected.map(id => options?.find(o => o.id === id)?.name).join(', ')}>
            {options?.map(option => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={fieldValue?.includes(option.id?.toString())} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      case "custom-input-component":
        return (
         field.customComponent
        );
    case "date":
      return <DatePicker label={!field.removeCaption ? `${field.label} ${isRequired ? ' *' : ''}` : null} value={fieldValue || null} readOnly={field.readonly} onChange={handleDateChange} renderInput={(params) => <TextField {...params} fullWidth error={isError} />} inputFormat="dd/MM/yyyy" />;
    case "datetime":
      return <DateTimePicker
        label={!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null} 
        value={fieldValue || null} readOnly={field.readonly} onChange={handleDateChange}
         renderInput={(params) => <TextField {...params} 
         label={!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null} 
         fullWidth error={isError} />} 
         inputFormat="dd/MM/yyyy HH:mm" ampm={false} />;
    case "checkbox":
      return <FormControlLabel control={<Checkbox checked={Boolean(fieldValue)}
        onChange={handleCheckboxChange}
        // disabled={field.readonly}
        name={field.name}
        inputProps={{ readOnly: field.readonly }} />}
        label={!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null} />;
    case "offlineAutocomplete":
      return (
        <Autocomplete
          options={options}
          getOptionLabel={(option) => option.name}
          fullWidth
          readOnly={field.readonly}
          onChange={(event, newValue) => {
            if (!field.readonly) {
              const newValueId = newValue ? newValue.id : '';
              setFieldValue(newValueId);
              setInputValue(newValue ? newValue.name : '');
              if (formik) {
                formik.setFieldValue(field.name, newValueId);
              }
              if (field.customChangeFunction) {
                field.customChangeFunction(newValue, formik);
              }
            }
          }}
          value={options.find(option => option.id === fieldValue) || null}
          renderInput={(params) => (
            <TextField
              {...params}
              label={!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null}
              error={isError}
              InputProps={{ ...params.InputProps, readOnly: field.readonly }}
            />
          )}
        />
      );
      case "html":
        const config = useMemo(
          () => ({
            readonly: false, // all options from https://xdsoft.net/jodit/docs/,
            placeholder: '',
            removeButtons: [],
            disablePlugins: ['speechRecognition'],
            buttons: [
              'bold',
              'strikethrough',
              'underline',
              'italic', '|',
              'ul',
              'ol', '|',
              'outdent', 'indent',  '|',
              'font',
              'fontsize',
              'brush',
              'paragraph', '|',
              'table',
              'link', '|',
              'align', 'undo', 'redo', '|',
              'hr',
              'eraser',
              'copyformat', '|',
              'symbol',
              'fullsize',
              'print',
          ],
          }),
          []
        );
        const editorRef = React.useRef(null);
        const [isExpanded, setIsExpanded] = useState(field.isExpanded); 
        const toggleExpand = () => {
          setIsExpanded(!isExpanded);
      };
  
      return (
        <Card>
          <CardHeader title={!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null} action={
          <>
            {
              field.expandable &&
              <IconButton onClick={toggleExpand}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            }
          </>
          }
          />
          <Divider />
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <EditorWrapper>
              <JoditEditor
                  ref={editorRef}
                  value={fieldValue}
                  config={config}
                  onBlur={newContent => handleEditorChange(newContent)}
              />
            </EditorWrapper>
          </CardContent>
            </Collapse>
        </Card>
      );

    case "autocomplete":
      return (
        <Autocomplete
          freeSolo
          disableClearable
          options={options}

          getOptionLabel={(option) => option.name || ''}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            if (!field.readonly) {
              setInputValue(newInputValue);
              if (formik.getFieldProps(field.name).value) {
                formik.setFieldValue(field.name, 0);
              }
              if (field.customInputChangeFunction) {
                field.customInputChangeFunction(newInputValue);
              }
            }
          }}
          onChange={(event, newValue) => {
            if (!field.readonly) {
              const newValueId = newValue ? newValue.id : '';
              setFieldValue(newValueId);
              setInputValue(newValue ? newValue.name : '');
              if (formik) {
                formik.setFieldValue(field.name, newValueId);
              }
              if (field.customChangeFunction) {
                field.customChangeFunction(newValue, formik);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={!field.removeCaption ? `${field.label}${isRequired ? ' *' : ''}` : null}
              fullWidth
              InputProps={{
                ...params.InputProps,
                type: 'search',
                readOnly: field.readonly,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                    {field.endItems}
                  </React.Fragment>
                )
              }}
              error={isError}
            />
          )}
        // disabled={field.readonly}
        />
      );
    default:
      return <TextField {...commonTextFieldProps} />;
  }
};
const EditorWrapper = styled(Box)(
  ({ theme }) => `

    .ql-editor {
      min-height: 100px;
    }

    .ql-snow .ql-picker {
      color: ${theme.colors.alpha.black[100]};
    }

    .ql-snow .ql-stroke {
      stroke: ${theme.colors.alpha.black[100]};
    }

    .ql-toolbar.ql-snow {
      border-top-left-radius: ${theme.general.borderRadius};
      border-top-right-radius: ${theme.general.borderRadius};
    }

    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      border-color: ${theme.colors.alpha.black[30]};
    }
.jodit-status-bar-link{
  display:none !important;
}
  .jodit-status-bar{
   display:none !important;
  }
    .ql-container.ql-snow {
      border-bottom-left-radius: ${theme.general.borderRadius};
      border-bottom-right-radius: ${theme.general.borderRadius};
    }

    &:hover {
      .ql-toolbar.ql-snow,
      .ql-container.ql-snow {
        border-color: ${theme.colors.alpha.black[50]};
      }
    }
`
);

export default DynamicField;
